<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  GroupIcon,
  VehicleAssetSliderVehicleIcon,
  CreditCardIcon,
  CheckFilledIcon,
  InfoIcon,
  WarningTriangleIcon,
} from '@ecx/ui/icons'
import { useBulkImportStore } from '@/stores/bulkImport'
import type { BulkImportEntity } from '@/types/bulkImport'
import BulkImportStatCard from '../shared/BulkImportStatCard.vue'
import resultPartialFolderUsers from '@/assets/bulkImport/result-partial-folder.svg'
import resultPartialFolderVehicles from '@/assets/bulkImport/result-partial-folder-vehicles.svg'
import resultPartialFolderCards from '@/assets/bulkImport/result-partial-folder-cards.png'
import resultSuccessFolder from '@/assets/bulkImport/result-success-folder.png'
import resultSuccessEditUsers from '@/assets/bulkImport/result-success-edit-users.svg'
import resultSuccessEditVehicles from '@/assets/bulkImport/result-success-edit-vehicles.svg'
import resultSuccessEditCards from '@/assets/bulkImport/result-success-edit-cards.png'
import iconMinus from '@/assets/bulkImport/icons/ic-ignored.svg'

const PARTIAL_ILLUSTRATIONS: Record<BulkImportEntity, string> = {
  users: resultPartialFolderUsers,
  vehicles: resultPartialFolderVehicles,
  cards: resultPartialFolderCards,
}

const EDIT_SUCCESS_ILLUSTRATIONS: Record<BulkImportEntity, string> = {
  users: resultSuccessEditUsers,
  vehicles: resultSuccessEditVehicles,
  cards: resultSuccessEditCards,
}

const props = defineProps<{
  variant: 'success' | 'partial'
}>()

const emit = defineEmits<{
  (e: 'navigate-users', importId: string): void
  (e: 'navigate-vehicles', importId: string): void
  (e: 'navigate-cards', importId: string): void
  (e: 'contact-support'): void
}>()

const { t } = useI18n()
const store = useBulkImportStore()

const totals = computed(() => store.totals)

const isVehiclesImport = computed(() => store.currentImport?.entity === 'vehicles')
const isCardsImport = computed(() => store.currentImport?.entity === 'cards')
const isEdit = computed(() => store.mode === 'edit')

const titleText = computed(() => {
  if (isSuccess.value) {
    if (isEdit.value) return t('corp.bulk_import.edit.result_success.title')
    return isVehiclesImport.value
      ? t('corp.bulk_import.result_success.title_vehicles')
      : t('corp.bulk_import.result_success.title')
  }
  return isEdit.value
    ? t('corp.bulk_import.edit.result_partial.title')
    : t('corp.bulk_import.result_partial.title')
})

const successSubtitle = computed(() => {
  if (isEdit.value) {
    if (isCardsImport.value) return t('corp.bulk_import.edit.result_success.subtitle_cards')
    return isVehiclesImport.value
      ? t('corp.bulk_import.edit.result_success.subtitle_vehicles')
      : t('corp.bulk_import.edit.result_success.subtitle')
  }
  return isVehiclesImport.value
    ? t('corp.bulk_import.result_success.subtitle_vehicles')
    : t('corp.bulk_import.result_success.subtitle')
})

function navigateToCreatedRecords() {
  const importId = store.currentImport?.id ?? ''
  if (isCardsImport.value) {
    emit('navigate-cards', importId)
  } else if (isVehiclesImport.value) {
    emit('navigate-vehicles', importId)
  } else {
    emit('navigate-users', importId)
  }
}

// Falhas de entidade e de cartão são conjuntos disjuntos; o card soma os dois.
const failureCount = computed(
  () => (totals.value?.failed ?? 0) + (store.currentImport?.cards_failed ?? 0),
)

// Código de suporte = id do import, correlacionável nos logs do backend.
const logId = computed(() => store.currentImport?.id ?? '-')

const isSuccess = computed(() => props.variant === 'success')

const importEntity = computed<BulkImportEntity>(() => store.currentImport?.entity ?? 'users')

const illustrationSrc = computed(() => {
  if (!isSuccess.value) return PARTIAL_ILLUSTRATIONS[importEntity.value]
  if (isEdit.value) return EDIT_SUCCESS_ILLUSTRATIONS[importEntity.value]
  return resultSuccessFolder
})
</script>

<template>
  <div class="flex flex-col flex-1 pb-6">
    <div
      class="flex flex-col items-center justify-center text-center gap-3 px-6"
      :class="isEdit ? 'flex-1 py-4' : isSuccess ? 'pt-16 pb-4' : 'pt-10 pb-4'"
      data-testid="result-hero"
    >
      <!-- success usa PNG porque o asset do Figma é rasterizado, não exporta SVG limpo -->
      <img
        :src="illustrationSrc"
        alt=""
        class="w-24 mb-2"
      />

      <h2 class="text-2xl font-semibold text-grey-950">
        {{ titleText }}
      </h2>

      <p v-if="isSuccess" class="text-sm text-grey-600 max-w-sm">
        {{ successSubtitle }}
      </p>

      <p v-else class="text-sm text-grey-600 max-w-sm text-center">
        {{ t('corp.bulk_import.result_partial.subtitle') }}
        <button
          type="button"
          class="text-royal-500 underline cursor-pointer"
          data-testid="support-link"
          @click="emit('contact-support')"
        >{{ t('corp.bulk_import.result_partial.support_link') }}</button>
        {{ t('corp.bulk_import.result_partial.log_code', { logId }) }}
      </p>
    </div>

    <div v-if="isEdit" class="flex shrink-0 gap-2 px-6 pt-6 pb-2">
      <BulkImportStatCard
        :value="totals?.succeeded ?? 0"
        :label="t('corp.bulk_import.edit.result.edited')"
        :icon="CheckFilledIcon"
        icon-color-class="text-green-500"
        icon-bg-class="bg-green-25"
        icon-ring-class="ring-1 ring-green-300"
        :navigable="true"
        @navigate="navigateToCreatedRecords"
      />

      <BulkImportStatCard
        :value="totals?.ignored ?? 0"
        :label="t('corp.bulk_import.edit.result.no_changes')"
        icon-bg-class="bg-grey-50"
        icon-ring-class="shadow-bulk-import-icon-ring-grey"
      >
        <template #icon>
          <img :src="iconMinus" alt="" class="w-5 h-5" />
        </template>
      </BulkImportStatCard>

      <BulkImportStatCard
        :value="failureCount"
        :label="t('corp.bulk_import.edit.result.failed')"
        icon-bg-class="bg-red-25"
        icon-ring-class="shadow-bulk-import-icon-ring-error"
      >
        <template #icon>
          <WarningTriangleIcon class="w-5 h-5 text-red-500" aria-hidden="true" />
        </template>
      </BulkImportStatCard>
    </div>

    <!-- stat labels usam result_success.* nas duas variantes de propósito; partial não tem chaves próprias -->
    <div v-else class="flex gap-2 px-6 pt-6 pb-2">
      <BulkImportStatCard
        :value="totals?.succeeded ?? 0"
        :label="isVehiclesImport ? t('corp.bulk_import.result_success.new_users_vehicles') : t('corp.bulk_import.result_success.new_users')"
        :icon="isVehiclesImport ? VehicleAssetSliderVehicleIcon : GroupIcon"
        icon-bg-class="bg-royal-25"
        :icon-color-class="isVehiclesImport ? 'text-royal-300' : 'text-royal-500'"
        icon-ring-class="ring-1 ring-royal-100"
        :navigable="true"
        @navigate="navigateToCreatedRecords"
      />

      <BulkImportStatCard
        :value="totals?.new_cards !== undefined ? totals.new_cards : '-'"
        :label="t('corp.bulk_import.result_success.new_cards')"
        :icon="CreditCardIcon"
        icon-bg-class="bg-royal-25"
        icon-color-class="text-royal-500"
        icon-ring-class="ring-1 ring-royal-100"
        :navigable="true"
        @navigate="emit('navigate-cards', store.currentImport?.id ?? '')"
      />

      <BulkImportStatCard
        :value="totals?.ignored ?? 0"
        :label="t('corp.bulk_import.result_success.ignored')"
        icon-bg-class="bg-grey-50"
        icon-ring-class="shadow-bulk-import-icon-ring-grey"
      >
        <template #icon>
          <img :src="iconMinus" alt="" class="w-5 h-5" />
        </template>
      </BulkImportStatCard>

      <BulkImportStatCard
        :value="failureCount"
        :label="t('corp.bulk_import.result_success.creation_failed')"
        icon-bg-class="bg-red-25"
        icon-ring-class="shadow-bulk-import-icon-ring-error"
      >
        <template #icon>
          <WarningTriangleIcon class="w-5 h-5 text-red-500" aria-hidden="true" />
        </template>
      </BulkImportStatCard>
    </div>

    <div
      v-if="!isSuccess"
      class="flex flex-col items-center justify-center bg-white border-b border-grey-100 pb-6 pt-2 px-6"
      :class="isEdit && 'shrink-0'"
      data-testid="report-hint-banner"
    >
      <div class="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg bg-royal-25 overflow-hidden">
        <InfoIcon class="w-5 h-5 text-royal-500 flex-shrink-0" aria-hidden="true" />
        <p class="text-label-xs text-royal-500 min-w-0 flex-1">
          {{ t('corp.bulk_import.result_partial.report_hint') }}
        </p>
      </div>
    </div>
  </div>
</template>
